import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Phone, Menu as MenuIcon, X } from "lucide-react";
import { BRAND, NAV_LINKS } from "@/data/content";
import { scrollToHash, isOpenNow } from "@/lib/ui";
import { Mark } from "@/components/Motif";
import { EASE } from "@/components/Reveal";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const openNow = isOpenNow();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, hash) => {
    e.preventDefault();
    setMenuOpen(false);
    scrollToHash(hash);
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled || menuOpen
          ? "bg-cocoa-950/85 backdrop-blur-md border-b border-gold/20"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-[72px] flex items-center justify-between gap-4">
        <a href="#home" onClick={(e) => go(e, "#home")} data-testid="nav-brand" className="flex items-center gap-3 shrink-0">
          <Mark className="w-8 h-8 text-gold" />
          <span className="leading-none">
            <span className="block font-display font-semibold tracking-[0.14em] text-cream text-sm sm:text-base">
              SAMIJ SWEETS
            </span>
            <span className="block text-[9px] uppercase tracking-[0.35em] text-gold/80 mt-1">
              Banskandi
            </span>
          </span>
        </a>

        <div className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <a
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              href={l.hash}
              onClick={(e) => go(e, l.hash)}
              className="text-sm text-cream/70 hover:text-gold transition-colors duration-200 tracking-wide"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <span
            data-testid="nav-open-status"
            className="hidden md:flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-cream/60"
          >
            <span className={`w-1.5 h-1.5 rounded-full ${openNow ? "bg-emerald-400" : "bg-saffron"} animate-pulse`} />
            {openNow ? "Open now" : "Opens 5 AM"}
          </span>
          <a
            data-testid="nav-call-button"
            href={`tel:${BRAND.phones[0]}`}
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-ink hover:bg-gold-light transition-colors duration-200"
          >
            <Phone className="w-4 h-4" />
            Call Now
          </a>
          <button
            data-testid="nav-menu-toggle"
            aria-label="Toggle menu"
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden text-cream p-2 -mr-2"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <MenuIcon className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden bg-cocoa-950/95 backdrop-blur-md border-b border-gold/20"
          >
            <div className="px-6 pt-4 pb-8 flex flex-col">
              {NAV_LINKS.map((l, i) => (
                <motion.a
                  key={l.id}
                  href={l.hash}
                  onClick={(e) => go(e, l.hash)}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4, ease: EASE }}
                  className="py-3.5 font-display text-2xl text-cream hover:text-gold transition-colors border-b border-gold/10"
                >
                  {l.label}
                </motion.a>
              ))}
              <a
                data-testid="nav-mobile-call-button"
                href={`tel:${BRAND.phones[0]}`}
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 text-sm font-semibold text-ink"
              >
                <Phone className="w-4 h-4" /> Call {BRAND.phones[0]}
              </a>
              <p className="mt-4 text-center text-[11px] uppercase tracking-[0.25em] text-cream/50">
                Open Daily {BRAND.hours}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
